import { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import { SchoolOutlined } from '@mui/icons-material';
import {
  collection,
  query,
  where,
  onSnapshot,
  documentId,
  getDocs,
  type DocumentData,
} from 'firebase/firestore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { enrollmentApi } from '../../../lib/api/enrollment.api';
import { db } from '../../../lib/firebase';
import { useTenantStore, useTenantTimeZone } from '../../../stores/tenant.store';
import { todayInZoneWeb } from '@dosfilos/shared';
import { sectionStarted } from '../../../lib/section-start';
import { formatDate } from '../../../lib/i18n-format';
import { errorText } from '../../../lib/error-text';
import { useTranslation } from 'react-i18next';
import { enrollmentColumns, type EnrollmentColumn } from './person-enrollments.columns';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import { isModuleEnabled } from '@dosfilos/domain';
import { formatGrade, gradePasses } from '../../../lib/grading';

interface PersonEnrollmentsTabProps {
  personId: string;
  /** Solo lectura y sin nota numérica (ver person-enrollments.columns). */
  importedRecord?: boolean;
}

const STATUS_COLOR: Record<string, 'primary' | 'success' | 'error' | 'default'> = {
  enrolled: 'primary',
  approved: 'success',
  completed: 'success',
  failed: 'error',
  dropped: 'error',
};

/** El color de la nota final, con la escala del seminario: verde si aprueba,
 *  rojo si no. Antes era «verde desde 7»: en 1-7 solo un 7 salía verde. */
const GRADE_COLOR = (passes: boolean | null) =>
  passes === null ? 'text.secondary' : passes ? 'success.main' : 'error.main';

export function PersonEnrollmentsTab({
  personId,
  importedRecord = false,
}: PersonEnrollmentsTabProps) {
  const { gradingScale } = useTenantConfig();
  const columns = enrollmentColumns({ importedRecord });
  const show = (c: EnrollmentColumn) => columns.includes(c);
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [enrollments, setEnrollments] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(false);

  const [dropDialogTarget, setDropDialogTarget] = useState<{
    id: string;
    courseName: string;
    /** H8: la sección ya empezó → la baja deja W; si no, no deja rastro. */
    started: boolean;
  } | null>(null);
  const todayYmd = todayInZoneWeb(useTenantTimeZone());
  const [dropNotice, setDropNotice] = useState<string | null>(null);
  const financeOn = isModuleEnabled(useTenantConfig().modules, 'finance');
  const [dropping, setDropping] = useState(false);

  const handleDrop = async () => {
    if (!dropDialogTarget || !currentTenantId) return;
    setDropping(true);
    try {
      const result = await enrollmentApi.dropEnrollment({
        tenantId: currentTenantId,
        enrollmentId: dropDialogTarget.id,
      });
      setDropDialogTarget(null);
      // H8: con una W el cobro no se recalcula (la devolución, si corresponde,
      // la decide Finanzas).
      if (result?.billing === 'keep' && financeOn)
        setDropNotice(t('sections.roster.wBillingNotice'));
    } catch (err: any) {
      console.error(err);
      alert(errorText(err, t('people.enrollmentsTab.dropError')));
    } finally {
      setDropping(false);
    }
  };

  useEffect(() => {
    if (!currentTenantId || !personId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const q = query(
      collection(db, `tenants/${currentTenantId}/enrollments`),
      where('studentId', '==', personId),
    );

    const unsub = onSnapshot(
      q,
      async (snap) => {
        try {
          const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

          // Cada documento relacionado se lee UNA vez, por lotes de ids: antes
          // eran 5 lecturas sueltas por inscripción (sección, curso, período,
          // docente, programa), repitiendo los mismos cursos y períodos.
          const byIds = async (name: string, ids: Iterable<string>) => {
            const unique = [...new Set([...ids].filter(Boolean))];
            const out = new Map<string, DocumentData>();
            for (let i = 0; i < unique.length; i += 30) {
              const snap = await getDocs(
                query(
                  collection(db, `tenants/${currentTenantId}/${name}`),
                  where(documentId(), 'in', unique.slice(i, i + 30)),
                ),
              );
              for (const d of snap.docs) out.set(d.id, d.data());
            }
            return out;
          };
          const sections = await byIds(
            'sections',
            raw.map((e: DocumentData) => e['sectionId'] as string),
          );
          const field = (k: string) =>
            [...sections.values()].map((s) => s[k] as string).filter(Boolean);
          const [courses, terms, people, programs] = await Promise.all([
            byIds('courses', field('courseId')),
            byIds('terms', field('termId')),
            byIds('people', field('facultyId')),
            byIds('programs', field('programId')),
          ]);

          const resolved = raw.map((e: DocumentData) => {
            const section = e['sectionId'] ? (sections.get(e['sectionId']) ?? null) : null;
            const faculty = section?.facultyId ? people.get(section.facultyId) : null;
            const facultyName = faculty
              ? `${faculty.firstName ?? ''} ${faculty.lastName ?? ''}`.trim() || '—'
              : '—';
            return {
              ...e,
              courseName: courses.get(section?.courseId)?.name ?? '—',
              termName: terms.get(section?.termId)?.name ?? '—',
              programName: programs.get(section?.programId)?.name ?? '—',
              // Section-level start/end dates — used for sorting and display
              sectionStartDate: section?.startDate ?? null,
              sectionEndDate: section?.endDate ?? null,
              // H8: para anticipar si la baja deja W (la sección ya empezó).
              sectionLifecycleOverride: section?.lifecycleOverride ?? null,
              termStartDate: terms.get(section?.termId)?.startDate ?? null,
              facultyName,
            };
          });

          // Sort by section startDate ascending (oldest first), then by enrolledAt as tiebreaker
          setEnrollments(
            resolved.sort((a: any, b: any) => {
              const toMs = (v: any): number => {
                if (!v) return 0;
                if (typeof v.toDate === 'function') return v.toDate().getTime();
                return new Date(v).getTime();
              };
              const diff = toMs(a.sectionStartDate) - toMs(b.sectionStartDate);
              if (diff !== 0) return diff;
              return toMs(a.enrolledAt) - toMs(b.enrolledAt);
            }),
          );
        } catch (err) {
          console.error('[PersonEnrollmentsTab] error:', err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('[PersonEnrollmentsTab] Firestore error:', err);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [currentTenantId, personId]);

  if (loading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  if (enrollments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <SchoolOutlined sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
        <Typography variant="h6">{t('people.enrollmentsTab.emptyTitle')}</Typography>
        <Typography variant="body2">{t('people.enrollmentsTab.emptyHelp')}</Typography>
      </Box>
    );
  }

  // Group enrollments by term
  const byTerm: Record<string, DocumentData[]> = {};
  enrollments.forEach((e: any) => {
    const key = e.termName || t('people.enrollmentsTab.noTerm');
    if (!byTerm[key]) byTerm[key] = [];
    byTerm[key].push(e);
  });

  return (
    <Stack spacing={3}>
      {dropNotice && (
        <Alert severity="info" onClose={() => setDropNotice(null)}>
          {dropNotice}
        </Alert>
      )}
      {Object.entries(byTerm).map(([term, items]) => (
        <Card key={term} variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ pb: '8px !important' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              📅 {term}
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: 12 } }}
                  >
                    <TableCell>{t('people.enrollmentsTab.columns.course')}</TableCell>
                    <TableCell>{t('people.enrollmentsTab.columns.program')}</TableCell>
                    <TableCell>{t('people.enrollmentsTab.columns.faculty')}</TableCell>
                    <TableCell align="center">{t('people.enrollmentsTab.columns.start')}</TableCell>
                    <TableCell align="center">{t('people.enrollmentsTab.columns.end')}</TableCell>
                    {show('grade') && (
                      <TableCell align="center">
                        {t('people.enrollmentsTab.columns.grade')}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {t('people.enrollmentsTab.columns.status')}
                    </TableCell>
                    {show('actions') && (
                      <TableCell align="right">
                        {t('people.enrollmentsTab.columns.actions')}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(items as any[]).map((e) => (
                    <TableRow key={e.id} sx={{ '& td': { color: 'text.primary' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{e.courseName}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {e.programName}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {e.facultyName}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}
                      >
                        {e.sectionStartDate
                          ? (() => {
                              const d =
                                typeof e.sectionStartDate.toDate === 'function'
                                  ? e.sectionStartDate.toDate()
                                  : new Date(e.sectionStartDate);
                              return formatDate(d, {
                                day: '2-digit',
                                month: 'short',
                                year: '2-digit',
                              });
                            })()
                          : '—'}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}
                      >
                        {e.sectionEndDate
                          ? (() => {
                              const d =
                                typeof e.sectionEndDate.toDate === 'function'
                                  ? e.sectionEndDate.toDate()
                                  : new Date(e.sectionEndDate);
                              return formatDate(d, {
                                day: '2-digit',
                                month: 'short',
                                year: '2-digit',
                              });
                            })()
                          : '—'}
                      </TableCell>
                      {show('grade') && (
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color: GRADE_COLOR(
                                gradePasses({ value: e.grade, label: e.gradeLabel }, gradingScale),
                              ),
                            }}
                          >
                            {/* `grade` y `gradeLabel` del documento: antes se leía un
                                `finalGrade` que la inscripción no tiene, y salía «—». */}
                            {formatGrade({ value: e.grade, label: e.gradeLabel }, gradingScale)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Chip
                          label={
                            // Solo lo que tiene clave: en producción una clave que
                            // falta se reemplaza por un aviso genérico, aun con
                            // `defaultValue`. Un estado desconocido va tal cual.
                            e.status in STATUS_COLOR
                              ? t(`people.enrollmentsTab.status.${e.status}`)
                              : (e.status ?? '—')
                          }
                          size="small"
                          color={STATUS_COLOR[e.status] ?? 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      {show('actions') && (
                        <TableCell align="right">
                          {e.status === 'enrolled' && (
                            <Tooltip title={t('people.enrollmentsTab.drop.action')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setDropDialogTarget({
                                    id: e.id,
                                    courseName: e.courseName,
                                    started: sectionStarted(
                                      {
                                        startDate: e.sectionStartDate,
                                        lifecycleOverride: e.sectionLifecycleOverride,
                                      },
                                      e.termStartDate,
                                      todayYmd,
                                    ),
                                  })
                                }
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}

      <Dialog
        open={!!dropDialogTarget}
        onClose={() => !dropping && setDropDialogTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('people.enrollmentsTab.drop.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('people.enrollmentsTab.drop.confirm', {
              course: dropDialogTarget?.courseName ?? '',
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {dropDialogTarget?.started
              ? t('people.enrollmentsTab.drop.helpW')
              : t('people.enrollmentsTab.drop.helpNoTrace')}
          </Typography>
          {dropDialogTarget?.started && financeOn && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('sections.roster.wBillingNotice')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDropDialogTarget(null)} color="inherit" disabled={dropping}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleDrop}
            color="error"
            variant="contained"
            disableElevation
            disabled={dropping}
          >
            {dropping ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('people.enrollmentsTab.drop.title')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
